import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { logAuthStatusScreenShown } from "../utils/singleOrderLayoutEventLogger";

/**
 * Fires the `AUTH_STATUS_SCREEN_SHOWN` analytics event exactly once for the
 * lifetime of the hook instance, after the Postgres-backed data accumulator
 * has populated all three slices read by the event payload:
 * `generalOrderDetails`, `coverageDetails`, and `authOnFileDetails`.
 *
 * The data accumulator loads each slice asynchronously and independently;
 * gating on all three (and listing each in the effect's dependency array)
 * prevents an early fire that would emit empty `auth_status` to BigQuery.
 *
 * The "log only once" semantics are enforced via a `useRef` boolean so that
 * subsequent re-renders triggered by other state changes do not re-emit
 * the event.
 */
export const useOrderDataProcessing = () => {
  const { generalOrderDetails, coverageDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const hasLoggedToBigQuery = useRef<boolean>(false);
  const orgId = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  useEffect(() => {
    if (generalOrderDetails && coverageDetails && authOnFileDetails) {
      if (!hasLoggedToBigQuery.current) {
        logAuthStatusScreenShown({
          orgId: orgId,
          userId: user?.id ?? undefined,
          userEmail: user?.email ?? undefined,
          payerName,
          generalOrderDetails: generalOrderDetails?.order ?? undefined,
          authOnFileDetails: authOnFileDetails?.auth_on_file ?? undefined,
          order_id: generalOrderDetails?.order?.order_id ?? "",
          patient_id:
            generalOrderDetails?.order?.demographics?.patient_id ?? "",
        });
        hasLoggedToBigQuery.current = true;
      }
    }
  }, [
    orgId,
    user,
    payerName,
    generalOrderDetails,
    coverageDetails,
    authOnFileDetails,
  ]);
};
